import { ActionVariantEnum, QuestionnaireQuestionFragment } from '@pm/graphql';
import { ButtonLink, Icon, IconButton } from '@pm/ui';
import { JSX, useMemo } from 'react';
import { useSpinDelay } from 'spin-delay';
import { overrideBrowserBack } from '../../utilities/backUtilities';
import { BackArrow } from '../BackArrow';
import { Card } from '../Card';
import LayoutWithBackButton from '../LayoutWithBackButton/LayoutWithBackButton';
import { actionVariantToIntent } from './OutcomePage';
import { QuestionnaireLoadingIndicator } from './QuestionnaireLoadingIndicator';
import { QuestionnairePageHeading } from './QuestionnairePageHeading';
import { QuestionnaireResources } from './QuestionnaireResources';
import { QuestionnaireSection } from './QuestionnaireSection';

type PageAction = {
  label: string;
  variant: ActionVariantEnum;
  to: string;
};

interface QuestionPageProps {
  children: React.ReactNode;
  title?: string | null;
  description?: string | null;
  onPreviousStep?: () => void;
  onQuestionnaireEscape?: () => void;
  isLoading?: boolean;
  actions?: PageAction[];
  questions: QuestionnaireQuestionFragment[];
}

export const QuestionPage = ({
  children,
  title,
  isLoading = false,
  description,
  onPreviousStep,
  onQuestionnaireEscape,
  actions = [],
  questions = [],
}: QuestionPageProps) => {
  const titleEmpty = title === '' || title == null;
  const descriptionEmpty = description === '' || description == null;
  const showHeading = !titleEmpty || !descriptionEmpty;

  const showLoadingIndicator = useSpinDelay(isLoading, {
    delay: 300,
    minDuration: 250,
    ssr: false,
  });

  const buildBelowPageResources = (
    questions: QuestionnaireQuestionFragment[],
  ) => {
    const allResources: (JSX.Element | null)[] = [];

    questions.map((question) => {
      if (question.resources) {
        allResources.push(
          <QuestionnaireResources
            key={question.id}
            resources={question.resources}
          />,
        );
      }
    });
    return allResources;
  };

  const allResources = useMemo(
    () => buildBelowPageResources(questions),
    [questions],
  );

  //To allow browser/mobile back button functionality
  overrideBrowserBack(onPreviousStep);

  return (
    <div className="space-y-4">
      <LayoutWithBackButton
        buttonNode={
          <div className="flex flex-row justify-between items-center">
            {onPreviousStep ? (
              <BackArrow disabled={isLoading} onClick={onPreviousStep} />
            ) : null}
            <IconButton
              aria-label="Close"
              intent="ghost"
              size="small"
              onClick={onQuestionnaireEscape}
            >
              <Icon name="close" />
            </IconButton>
          </div>
        }
      >
        <Card className="relative select-none space-y-8">
          {showHeading && (
            <QuestionnaireSection>
              <QuestionnairePageHeading
                title={title as string}
                description={description as string}
              />
            </QuestionnaireSection>
          )}
          {children}
          {showLoadingIndicator && <QuestionnaireLoadingIndicator />}
          {actions.length > 0 &&
            actions.map(({ label, variant, to }) => (
              <ButtonLink
                key={label}
                to={to}
                intent={actionVariantToIntent[variant]}
                external={to.startsWith('https')}
              >
                {label}
              </ButtonLink>
            ))}
        </Card>
      </LayoutWithBackButton>
      {allResources}
    </div>
  );
};
